import React, { useMemo, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, PencilIcon, PlusIcon, SearchIcon, Trash2Icon } from 'lucide-react';
import { PRODUCTS } from '../../data/products';
import { Product } from '../../types';
import { formatPrice } from '../../utils/format';
interface ProductsTableProps {
  onCreate: () => void;
  onEdit: (product: Product) => void;
}
const PAGE_SIZE = 6;
export function ProductsTable({
  onCreate,
  onEdit
}: ProductsTableProps) {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [inactive, setInactive] = useState<string[]>(['p8']);
  const filtered = useMemo(() => PRODUCTS.filter(p => `${p.name} ${p.categoryLabel}`.toLowerCase().includes(query.toLowerCase())), [query]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const rows = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  return <section className="border border-white/8 bg-ink-700" data-id="element-458" data-mp="1hfylvwa7i8yj">
      <div className="flex flex-col gap-4 border-b border-white/8 p-5 sm:flex-row sm:items-center sm:justify-between" data-id="element-459" data-mp="1hggqphaxpvi2">
        <div data-id="element-460" data-mp="17hhp2iol704k">
          <h2 className="font-display text-2xl uppercase tracking-wide text-white" data-id="element-461" data-mp="17hrea41t5397">
            Productos
          </h2>
          <p className="mt-1 text-xs text-white/40" data-id="element-462" data-mp="1cfsvskjl4uik">
            {filtered.length} productos en el catálogo
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-463" data-mp="xqepxmf7wv7x">
          <div className="relative" data-id="element-464" data-mp="12j7t45v1fvhw">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" data-id="element-465" data-mp="12kiszuld4j3q" />
            <input type="search" value={query} onChange={e => {
            setQuery(e.target.value);
            setPage(1);
          }} placeholder="Buscar producto..." aria-label="Buscar producto" className="w-full border border-white/10 bg-ink-900 py-2.5 pl-9 pr-3 text-sm text-white placeholder:text-white/30 focus:border-pink-500 focus:outline-none sm:w-56" data-id="element-466" data-mp="tpusehqqfpl9" />
          </div>
          <button type="button" onClick={onCreate} className="flex items-center justify-center gap-2 bg-pink-500 px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-pink-600" data-id="element-467" data-mp="3n2w8oyjnxr21">
            <PlusIcon className="h-4 w-4" data-id="element-468" data-mp="jlec5n9cc6gx" /> Agregar producto
          </button>
        </div>
      </div>

      <div className="overflow-x-auto" data-id="element-469" data-mp="el03c2zx0shu">
        <table className="w-full min-w-[860px] text-left text-sm" data-id="element-470" data-mp="9ngph474qhqf">
          <thead data-id="element-471" data-mp="9pt5vcx8ok1d">
            <tr className="border-b border-white/8 text-[10px] uppercase tracking-[0.16em] text-white/40" data-id="element-472" data-mp="4p5g0ukqy98q">
              <th scope="col" className="px-5 py-3 font-semibold" data-id="element-473" data-mp="2ueqme7cynu03">
                Imagen
              </th>
              <th scope="col" className="px-5 py-3 font-semibold" data-id="element-474" data-mp="2peuu35dmlr0d">
                Nombre
              </th>
              <th scope="col" className="px-5 py-3 font-semibold" data-id="element-475" data-mp="14y4u668zvxh8">
                Categoría
              </th>
              <th scope="col" className="px-5 py-3 font-semibold" data-id="element-476" data-mp="19wy8u200puiz">
                Precio
              </th>
              <th scope="col" className="px-5 py-3 font-semibold" data-id="element-477" data-mp="1etopaqfqis4x">
                Stock
              </th>
              <th scope="col" className="px-5 py-3 font-semibold" data-id="element-478" data-mp="1jnaq9lcjd7kw">
                Estado
              </th>
              <th scope="col" className="px-5 py-3 text-right font-semibold" data-id="element-479" data-mp="1on5iptbym582">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody data-id="element-480" data-mp="1xlhee9ront7d">
            {rows.map(product => {
            const isActive = !inactive.includes(product.id);
            const low = product.stock <= 8;
            return <tr key={product.id} className="border-b border-white/5 transition-colors last:border-0 hover:bg-white/[0.02]" data-id="element-481" data-mp="389linn9prod">
                  <td className="px-5 py-3" data-id="element-482" data-mp="1ynoc0j6xuwgw">
                    <img src={product.images[0]} alt={product.name} className="h-11 w-11 object-cover" data-id="element-483" data-mp="1ymn4gop4ss3x" />
                  </td>
                  <td className="px-5 py-3" data-id="element-484" data-mp="27ntkkwnvc0wn">
                    <p className="font-semibold text-white" data-id="element-485" data-mp="23mhrui9p6zk8">{product.name}</p>
                    <p className="text-xs text-white/35" data-id="element-486" data-mp="23l6ky9kyhbow">SKU-{product.id.toUpperCase()}</p>
                  </td>
                  <td className="px-5 py-3" data-id="element-487" data-mp="1aylwphzs1fpk">
                    <span className="border border-white/12 bg-ink-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-white/70" data-id="element-488" data-mp="1azx19k11qrhq">
                      {product.categoryLabel}
                    </span>
                  </td>
                  <td className="px-5 py-3" data-id="element-489" data-mp="w9fz1ygl7xjs">
                    <span className="text-white" data-id="element-490" data-mp="w84wu32sixus">
                      {formatPrice(product.salePrice ?? product.price)}
                    </span>
                    {product.salePrice && <span className="ml-2 text-xs text-white/30 line-through" data-id="element-491" data-mp="i2w5sawvp1s2">
                        {formatPrice(product.price)}
                      </span>}
                  </td>
                  <td className="px-5 py-3" data-id="element-492" data-mp="872lg5cx43zq">
                    <span className={`font-semibold ${low ? 'text-red-400' : 'text-white'}`} data-id="element-493" data-mp="d4uex3t7ny61">
                      {product.stock}
                    </span>
                  </td>
                  <td className="px-5 py-3" data-id="element-494" data-mp="15sqjeup0uudd">
                    <button type="button" role="switch" aria-checked={isActive} aria-label={`${product.name} ${isActive ? 'activo' : 'inactivo'}`} onClick={() => setInactive(prev => prev.includes(product.id) ? prev.filter(id => id !== product.id) : [...prev, product.id])} className={`relative h-6 w-11 rounded-full transition-colors ${isActive ? 'bg-pink-500' : 'bg-ink-500'}`} data-id="element-495" data-mp="qwr1ytg0vhvk">
                      <span className={`absolute top-1 h-4 w-4 rounded-full bg-white transition-all ${isActive ? 'left-6' : 'left-1'}`} data-id="element-496" data-mp="18derel6ji3ce" />
                    </button>
                  </td>
                  <td className="px-5 py-3" data-id="element-497" data-mp="ueybc3msf0hl">
                    <div className="flex items-center justify-end gap-2" data-id="element-498" data-mp="ufzw4ztlv1ci">
                      <button type="button" onClick={() => onEdit(product)} aria-label={`Editar ${product.name}`} className="border border-white/10 p-2 text-white/60 transition-colors hover:border-pink-500 hover:text-pink-500" data-id="element-499" data-mp="pgwr3fdmkv4j">
                        <PencilIcon className="h-3.5 w-3.5" data-id="element-500" data-mp="2wb75e4ej6jks" />
                      </button>
                      <button type="button" aria-label={`Eliminar ${product.name}`} className="border border-white/10 p-2 text-white/60 transition-colors hover:border-red-500 hover:text-red-500" data-id="element-501" data-mp="312pa93jod4b1">
                        <Trash2Icon className="h-3.5 w-3.5" data-id="element-502" data-mp="2dcusq8oeurkg" />
                      </button>
                    </div>
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/8 p-5" data-id="element-503" data-mp="2nc28f2imayaq">
        <p className="text-xs text-white/40" data-id="element-504" data-mp="3tq1kqfck9lnz">
          Página {current} de {pages}
        </p>
        <div className="flex items-center gap-1" data-id="element-505" data-mp="2jqp4yqbld41">
          <button type="button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={current === 1} aria-label="Página anterior" className="border border-white/10 p-2 text-white/60 transition-colors hover:border-white/40 hover:text-white disabled:opacity-30" data-id="element-506" data-mp="2mmo8qdwm7u2">
            <ChevronLeftIcon className="h-4 w-4" data-id="element-507" data-mp="snnib8uaituu" />
          </button>
          {Array.from({
          length: pages
        }, (_, i) => i + 1).map(p => <button key={p} type="button" onClick={() => setPage(p)} aria-current={p === current ? 'page' : undefined} className={`h-9 w-9 border text-sm transition-colors ${p === current ? 'border-pink-500 bg-pink-500 text-white' : 'border-white/10 text-white/60 hover:border-white/40 hover:text-white'}`} data-id="element-508" data-mp="16dipq2dhl86g">
              {p}
            </button>)}
          <button type="button" onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={current === pages} aria-label="Página siguiente" className="border border-white/10 p-2 text-white/60 transition-colors hover:border-white/40 hover:text-white disabled:opacity-30" data-id="element-509" data-mp="3d7w3zrdcs9ab">
            <ChevronRightIcon className="h-4 w-4" data-id="element-510" data-mp="1nw940atf7gqf" />
          </button>
        </div>
      </div>
    </section>;
}